/**
 * useAdmin Hook
 *
 * Provides admin operations for managing organizations.
 * Only available to super admins.
 *
 * @module hooks/useAdmin
 */

import { useState, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/lib/firebase';

// =============================================================================
// Types
// =============================================================================

export interface OrganizationSummary {
  id: string;
  name: string;
  slug: string;
  accountType: 'standard' | 'internal';
  planId: string;
  ownerEmail?: string;
  memberCount: number;
  createdAt: string;
  usageThisPeriod: number;
  lifetimeUsage: number;
}

interface ListOrganizationsRequest {
  limit?: number;
  startAfter?: string;
  search?: string;
  accountType?: 'standard' | 'internal';
}

interface ListOrganizationsResponse {
  organizations: OrganizationSummary[];
  nextCursor?: string;
  totalCount?: number;
}

interface SetAccountTypeRequest {
  organizationId: string;
  accountType: 'standard' | 'internal';
}

interface SetAccountTypeResponse {
  success: boolean;
}

export interface UseAdminResult {
  /** List of organizations */
  organizations: OrganizationSummary[];
  /** Loading state */
  loading: boolean;
  /** Error message if any */
  error: string | null;
  /** Cursor for next page */
  nextCursor?: string;
  /** Fetch organizations */
  fetchOrganizations: (options?: ListOrganizationsRequest) => Promise<void>;
  /** Load more organizations */
  loadMore: () => Promise<void>;
  /** Set account type for an organization */
  setAccountType: (orgId: string, accountType: 'standard' | 'internal') => Promise<void>;
  /** Refresh the list */
  refresh: () => void;
}

// =============================================================================
// Hook
// =============================================================================

export function useAdmin(): UseAdminResult {
  const [organizations, setOrganizations] = useState<OrganizationSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | undefined>();
  const [lastOptions, setLastOptions] = useState<ListOrganizationsRequest>({});

  // ---------------------------------------------------------------------------
  // Fetch Organizations
  // ---------------------------------------------------------------------------

  const fetchOrganizations = useCallback(
    async (options: ListOrganizationsRequest = {}) => {
      setLoading(true);
      setError(null);
      setLastOptions(options);

      try {
        const listOrganizations = httpsCallable<
          ListOrganizationsRequest,
          ListOrganizationsResponse
        >(functions, 'listOrganizations');

        const result = await listOrganizations(options);

        setOrganizations(result.data.organizations);
        setNextCursor(result.data.nextCursor);
      } catch (err) {
        console.error('Error fetching organizations:', err);
        const message = err instanceof Error ? err.message : 'Failed to load organizations.';
        setError(message);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // ---------------------------------------------------------------------------
  // Load More
  // ---------------------------------------------------------------------------

  const loadMore = useCallback(async () => {
    if (!nextCursor || loading) return;

    setLoading(true);
    setError(null);

    try {
      const listOrganizations = httpsCallable<
        ListOrganizationsRequest,
        ListOrganizationsResponse
      >(functions, 'listOrganizations');

      const result = await listOrganizations({
        ...lastOptions,
        startAfter: nextCursor,
      });

      setOrganizations((prev) => [...prev, ...result.data.organizations]);
      setNextCursor(result.data.nextCursor);
    } catch (err) {
      console.error('Error loading more organizations:', err);
      const message = err instanceof Error ? err.message : 'Failed to load more.';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [nextCursor, loading, lastOptions]);

  // ---------------------------------------------------------------------------
  // Set Account Type
  // ---------------------------------------------------------------------------

  const setAccountType = useCallback(
    async (orgId: string, accountType: 'standard' | 'internal') => {
      try {
        const setAccountTypeFunc = httpsCallable<
          SetAccountTypeRequest,
          SetAccountTypeResponse
        >(functions, 'setAccountType');

        await setAccountTypeFunc({ organizationId: orgId, accountType });

        // Update local state
        setOrganizations((prev) =>
          prev.map((org) =>
            org.id === orgId ? { ...org, accountType } : org
          )
        );
      } catch (err) {
        console.error('Error setting account type:', err);
        throw err;
      }
    },
    []
  );

  // ---------------------------------------------------------------------------
  // Refresh
  // ---------------------------------------------------------------------------

  const refresh = useCallback(() => {
    fetchOrganizations(lastOptions);
  }, [fetchOrganizations, lastOptions]);

  // ---------------------------------------------------------------------------
  // Return
  // ---------------------------------------------------------------------------

  return {
    organizations,
    loading,
    error,
    nextCursor,
    fetchOrganizations,
    loadMore,
    setAccountType,
    refresh,
  };
}
